import type { IRecord } from "@gp/types/global";

const METRIC_KEYS = [
  'current_month_staff_rate',
  'ytm_staff_cost',
  'ytm_staff_cost_overhead',
  'ytm_staff_cost_marketing',
  'ytm_staff_cost_dev',
];

const METRIC_TIPS_CFG_MAPPING: IRecord = {
  current_month_staff_rate: {
    definition: 'Monthly Staff Cost / In House Headcount',
    source: 'Studio Reporting Package',
    updateCadence: 'Monthly',
  },
  ytm_staff_cost: {
    definition: 'Staff cost to the month.',
    source: 'Studio Reporting Package',
    updateCadence: 'Monthly',
  },
  ytm_staff_cost_overhead: {
    definition: 'Other staff cost to the month.',
    source: 'Studio Reporting Package',
    updateCadence: 'Monthly',
  },
  ytm_staff_cost_marketing: {
    definition: 'Marketing related staff cost to the month.',
    source: 'Studio Reporting Package',
    updateCadence: 'Monthly',
  },
  ytm_staff_cost_dev: {
    definition: 'Development related staff cost to the month.',
    source: 'Studio Reporting Package',
    updateCadence: 'Monthly',
  },
};

const TABLE_HEAD_MAP: IAllStudioPeopleTableHeaderOptType = {
  'bo': {
    'keyName': 'bo',
    'title': ['BO', ''],
    'align': 'left',
    'width': '150px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': false,
    'isFormat': false,
  },
  'studio': {
    'keyName': 'studio',
    'title': ['Studio', ''],
    'align': 'left',
    'width': '200px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': false,
    'isFormat': false,
  },
  'nearest_year_fte': {
    'keyName': 'nearest_year_fte',
    'title': ['-', 'FTE'],
    'align': 'right',
    'width': '160px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': false,
  },
  'nearest_month_fte': {
    'keyName': 'nearest_month_fte',
    'title': ['-', 'FTE'],
    'align': 'right',
    'width': '160px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': false,
  },
  'current_year_fte': {
    'keyName': 'current_year_fte',
    'title': ['- FTE', '(EOY)'],
    'align': 'right',
    'width': '160px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': false,
  },
  'nearest_year_staff_cost': {
    'keyName': 'nearest_year_staff_cost',
    'title': ['- Annual', 'Staff Cost($)'],
    'align': 'right',
    'width': '160px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': true,
  },
  'current_year_budget_staff_cost': {
    'keyName': 'current_year_budget_staff_cost',
    'title': ['- Annual', 'Budget Staff Cost($)'],
    'align': 'right',
    'width': '225px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': true,
  },
  'current_month_staff_cost': {
    'keyName': 'current_month_staff_cost',
    'title': ['-', 'Staff Cost($)'],
    'align': 'right',
    'width': '160px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': true,
  },
  'current_month_staff_rate': {
    'keyName': 'current_month_staff_rate',
    'title': ['-', 'Monthly Staff Rate($)'],
    'align': 'right',
    'width': '235px',
    'isGroup': false,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': true,
  },
  'ytm_staff_cost': {
    'keyName': 'ytm_staff_cost',
    'title': ['YTM Staff Cost($)', ''],
    'align': 'right',
    'width': '205px',
    'isGroup': true,
    'groupKey': '',
    'isValueCol': true,
    'isFormat': true,
  },
  'ytm_staff_cost_overhead': {
    'keyName': 'ytm_staff_cost_overhead',
    'title': ['YTM Staff Cost', 'Overhead($)'],
    'align': 'right',
    'width': '190px',
    'isGroup': false,
    'groupKey': 'ytm_staff_cost',
    'isValueCol': true,
    'isFormat': true,
  },
  'ytm_staff_cost_marketing': {
    'keyName': 'ytm_staff_cost_marketing',
    'title': ['YTM Staff Cost', 'Marketing($)'],
    'align': 'right',
    'width': '190px',
    'isGroup': false,
    'groupKey': 'ytm_staff_cost',
    'isValueCol': true,
    'isFormat': true,
  },
  'ytm_staff_cost_dev': {
    'keyName': 'ytm_staff_cost_dev',
    'title': ['YTM Staff Cost', 'Development($)'],
    'align': 'right',
    'width': '190px',
    'isGroup': false,
    'groupKey': 'ytm_staff_cost',
    'isValueCol': true,
    'isFormat': true,
  },
};

const FIRST_ROW_OPT: {
  'keyName': string,         // 对应字段
  'width': number,           // 宽度
  'format': 0 | 1 | 2,       // 0: 不需要处理, 1: 数值, 2: 需要转换B/M/K
  'align': 'left' | 'right', // 水平对齐方式
  'isFoldCol': boolean,      // 是否是折叠列
}[] = [
  {
    'keyName': 'boName',
    'width': 150,
    'format': 0,
    'align': 'left',
    'isFoldCol': false,
  },
  {
    'keyName': 'studioName',
    'width': 200,
    'format': 0,
    'align': 'left',
    'isFoldCol': false,
  },
  {
    'keyName': 'nearest_year_fte',
    'width': 160,
    'format': 1,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'nearest_month_fte',
    'width': 160,
    'format': 1,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'current_year_fte',
    'width': 160,
    'format': 1,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'nearest_year_staff_cost',
    'width': 160,
    'format': 2,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'current_year_budget_staff_cost',
    'width': 225,
    'format': 2,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'current_month_staff_cost',
    'width': 160,
    'format': 2,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'current_month_staff_rate',
    'width': 235,
    'format': 2,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'ytm_staff_cost',
    'width': 205,
    'format': 2,
    'align': 'right',
    'isFoldCol': false,
  },
  {
    'keyName': 'ytm_staff_cost_overhead',
    'width': 190,
    'format': 2,
    'align': 'right',
    'isFoldCol': true,
  },
  {
    'keyName': 'ytm_staff_cost_marketing',
    'width': 190,
    'format': 2,
    'align': 'right',
    'isFoldCol': true,
  },
  {
    'keyName': 'ytm_staff_cost_dev',
    'width': 190,
    'format': 2,
    'align': 'right',
    'isFoldCol': true,
  },
];

// 默认收起子列
const TABLE_SUB_KEY: string[] = [
  'ytm_staff_cost_overhead',
  'ytm_staff_cost_marketing',
  'ytm_staff_cost_dev'
]

export {
  TABLE_HEAD_MAP,
  TABLE_SUB_KEY,
  FIRST_ROW_OPT,
  METRIC_KEYS,
  METRIC_TIPS_CFG_MAPPING,
}
